import { useCallback, useEffect, useRef, useState } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import {
  HOSTED_PREVIEW_ENTITY,
  HOSTED_PREVIEW_READY,
  HOSTED_PREVIEW_DATA,
} from '@charitystack/shared';
import { withPreviewPlaceholders, type HostedPreviewData } from './previewPlaceholders';

// True device viewports. Media queries inside the iframe respond to THESE,
// not the dashboard window — that's the whole point of the iframe preview.
const VIEWPORTS = {
  desktop: { width: 1280, height: 832 },
  mobile: { width: 390, height: 844 },
} as const;

type DeviceKey = keyof typeof VIEWPORTS;

const DEVICES: { key: DeviceKey; label: string; Icon: typeof Monitor }[] = [
  { key: 'desktop', label: 'Desktop preview', Icon: Monitor },
  { key: 'mobile', label: 'Mobile preview', Icon: Smartphone },
];

const SEND_DEBOUNCE_MS = 150;

// blob: object URLs are origin-scoped — unreadable inside the iframe in dev
// (different ports). Convert any blob image field to a base64 data URL.
async function withPortableImage(
  previewData: HostedPreviewData | null | undefined,
): Promise<HostedPreviewData | null | undefined> {
  const image = previewData?.image;
  if (!previewData || typeof image !== 'string' || !image.startsWith('blob:')) return previewData;
  try {
    const blob = await fetch(image).then((r) => r.blob());
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { ...previewData, image: dataUrl };
  } catch {
    // Unreadable blob — drop it rather than send a URL the iframe can't load
    return { ...previewData, image: null };
  }
}

interface HostedPagePreviewFrameProps {
  previewData: HostedPreviewData;
}

interface PaneSize {
  width: number;
  height: number;
}

/**
 * HostedPagePreviewFrame — renders the real hosted page in an iframe at a
 * true device viewport, scaled to fit the pane, and streams the form
 * builder's unsaved previewData into it via postMessage.
 */
export default function HostedPagePreviewFrame({ previewData }: HostedPagePreviewFrameProps) {
  const [device, setDevice] = useState<DeviceKey>('desktop');
  const [paneSize, setPaneSize] = useState<PaneSize | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const hostedOrigin = import.meta.env.VITE_APP_HOSTED_PAGE_ORIGIN || window.location.origin;
  const previewUrl = `${hostedOrigin}/donate/preview`;

  // The same-origin fallback is correct on Amplify (mono-build, one domain)
  // but wrong in local dev, where it makes the iframe load the dashboard
  // itself. env-cmd snapshots .env-cmdrc.json at server start, so the usual
  // cause is a dev server started before VITE_APP_HOSTED_PAGE_ORIGIN existed.
  useEffect(() => {
    if (import.meta.env.DEV && !import.meta.env.VITE_APP_HOSTED_PAGE_ORIGIN) {
      console.warn(
        '[HostedPagePreviewFrame] VITE_APP_HOSTED_PAGE_ORIGIN is not set — the preview iframe ' +
          'will load the dashboard itself. Restart the dashboard dev server to pick it up from ' +
          'packages/dashboard/.env-cmdrc.json.',
      );
    }
  }, []);
  const viewport = VIEWPORTS[device];

  // Track the pane size; scale and frame dimensions derive from it.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setPaneSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Desktop fits the pane WIDTH and simulates a viewport tall enough to fill
  // the pane exactly (page content scrolls inside the iframe, like a real
  // browser window). Mobile keeps the phone's fixed aspect, fit to the pane.
  const scale = (() => {
    if (!paneSize || paneSize.width <= 0 || paneSize.height <= 0) return 1;
    if (device === 'desktop') return Math.min(paneSize.width / viewport.width, 1);
    return Math.min(paneSize.width / viewport.width, paneSize.height / viewport.height, 1);
  })();
  const frameHeight =
    device === 'desktop' && paneSize ? Math.round(paneSize.height / scale) : viewport.height;

  // Skip resends when nothing changed: the previewers build previewData as a
  // fresh object literal every render, so identity comparison is useless —
  // compare serialized content instead.
  const lastSentRef = useRef<string | null>(null);

  const sendPreviewData = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!iframeRef.current?.contentWindow || !previewData) return;
      const serialized = JSON.stringify(previewData);
      if (!force && serialized === lastSentRef.current) return;
      lastSentRef.current = serialized;
      try {
        const portable = await withPortableImage(withPreviewPlaceholders(previewData));
        // Re-read after the await — the iframe could have been torn down
        // (unmount, tab switch) while the image was being serialized.
        const target = iframeRef.current?.contentWindow;
        if (!target) return;
        target.postMessage(
          { action: HOSTED_PREVIEW_DATA, entity: HOSTED_PREVIEW_ENTITY, previewData: portable },
          hostedOrigin,
        );
      } catch (error) {
        console.error('[HostedPagePreviewFrame] failed to send preview data', error);
      }
    },
    [previewData, hostedOrigin],
  );

  // Handshake: wait for the iframe's READY, then send the current state.
  // force: a reloaded iframe (refresh, error recovery) re-announces READY and
  // must receive data again even though the serialized content is unchanged.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== hostedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const { action, entity } = event.data || {};
      if (entity !== HOSTED_PREVIEW_ENTITY || action !== HOSTED_PREVIEW_READY) return;
      setIframeReady(true);
      void sendPreviewData({ force: true });
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hostedOrigin, sendPreviewData]);

  // Stream edits (debounced) once the iframe is listening.
  useEffect(() => {
    if (!iframeReady) return undefined;
    const timer = setTimeout(() => void sendPreviewData(), SEND_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [iframeReady, sendPreviewData]);

  return (
    <div className="flex h-full flex-col pt-2">
      {/* Device toggle */}
      <div className="mx-auto mb-3 flex gap-1 rounded-lg border border-edge bg-paper-inset p-0.5">
        {DEVICES.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setDevice(key)}
            aria-label={label}
            title={label}
            className={`rounded-md px-3 py-1.5 focus:outline-none ${
              device === key
                ? 'border border-edge bg-white text-ink'
                : 'border border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>

      {/* Scaled true-viewport iframe. Padding lives on the outer wrapper so the
          measured (ref'd) box stays padding-free — clientWidth/Height include
          padding, which would otherwise oversize the iframe. */}
      <div className="min-h-0 flex-1 p-2">
        <div ref={containerRef} className="flex h-full w-full items-start justify-center">
          <div
            className="overflow-hidden rounded-lg shadow-2xl"
            style={{ width: viewport.width * scale, height: frameHeight * scale }}
          >
            <iframe
              ref={iframeRef}
              title="Hosted page preview"
              src={previewUrl}
              // Belt-and-suspenders against a missed READY (e.g. listener races
              // a very fast same-origin load): onLoad also pushes current state.
              // PreviewHost registers its listener during mount effects, which
              // run before the document's load event, so this cannot be too early.
              onLoad={() => void sendPreviewData({ force: true })}
              style={{
                width: viewport.width,
                height: frameHeight,
                border: 'none',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
